import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { MdClose } from '@teamworksdev/icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Team = { id: string; name: string };

/**
 * The type of transfer to perform. Both transfer tracks
 * to a new team.
 * - athleteTransfer: This type is event driven and only appears when the athlete is transferred to a new team.
 * - multiTeamTransfer: This type is static and appears when the athlete is on multiple teams.
 *
 * Note: The doNothing button is only shown for athleteTransfer type.
 */
type TransferType = 'athleteTransfer' | 'multiTeamTransfer';

interface MoveTrackModalProps {
  open: boolean;
  onClose: () => void;
  transferType: TransferType;
  trackName: string;
  otherTeams: Team[];

  onConfirm: (newTeamId: string, doNothing: boolean) => void;
}

export function MoveTrackModal({
  open,
  onClose,
  transferType,
  trackName,
  otherTeams,
  onConfirm,
}: MoveTrackModalProps) {
  const { t } = useTranslation();
  const [selectedTeam, setSelectedTeam] = useState<Team>(
    otherTeams[0] ?? { id: '', name: '' },
  );
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle className="flex items-center justify-between p-6">
        {t('moveTrack')}
        <IconButton onClick={onClose} aria-label={t('close')}>
          <MdClose size={24} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography className="text-base mb-4">
          {t('moveTrackConfirmation', {
            track: trackName,
            team: selectedTeam.name,
          })}
        </Typography>
        {transferType === 'athleteTransfer' && (
          <Typography className="text-sm text-gray-600">
            {t('doNotMoveTrackMessage')}
          </Typography>
        )}
        {otherTeams.length > 1 && (
          <Select
            value={selectedTeam.id || ''}
            onChange={(e) => {
              setSelectedTeam(
                otherTeams.find((t) => t.id === e.target.value) ?? {
                  id: '',
                  name: '',
                },
              );
            }}
          >
            {otherTeams.map((t) => (
              <MenuItem value={t.id} key={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        )}
      </DialogContent>

      <DialogActions className="justify-between">
        {transferType === 'athleteTransfer' && (
          <Button
            variant="outlined"
            onClick={() => onConfirm(selectedTeam.id, true)}
          >
            {t('noAction')}
          </Button>
        )}
        <div className="flex space-x-4 ml-auto">
          <Button variant="outlined" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => onConfirm(selectedTeam.id, false)}
          >
            {t('yes')}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}
